import { useConfig } from 'wagmi'
import { getTransactionCount } from 'wagmi/actions'

import { isSolanaChain } from '@cowprotocol/cow-sdk'
import { useGnosisSafeInfo, useWalletInfo } from '@cowprotocol/wallet'

import { useAppKitConnection } from '@reown/appkit-adapter-solana/react'
import { useBlockNumber } from 'entities/blockchain'
import { useAsyncMemo } from 'use-async-memo'

import { useGetSafeTxInfo } from 'legacy/hooks/useGetSafeTxInfo'
import { useAppDispatch } from 'legacy/state/hooks'
import { useCancelOrdersBatch } from 'legacy/state/orders/hooks'

import { useGetTwapOrderById } from 'modules/twap/hooks/useGetTwapOrderById'

import { SOLANA_UNUSED_NONCE } from 'common/constants/common'
import { useGetReceipt } from 'common/hooks/useGetReceipt'
import useNativeCurrency from 'lib/hooks/useNativeCurrency'

import { CheckEthereumTransactions } from '../types'

export function usePendingTransactionsContext(hasPendingTxs: boolean): CheckEthereumTransactions | null {
  const config = useConfig()
  const { chainId, account } = useWalletInfo()
  const safeInfo = useGnosisSafeInfo()
  const isSafeWallet = !!safeInfo
  const lastBlockNumber = useBlockNumber()

  const dispatch = useAppDispatch()
  const cancelOrdersBatch = useCancelOrdersBatch()
  const getReceipt = useGetReceipt(chainId)
  const getTxSafeInfo = useGetSafeTxInfo()
  const getTwapOrderById = useGetTwapOrderById()
  const nativeCurrencySymbol = useNativeCurrency().symbol || 'ETH'
  const { connection: solanaConnection } = useAppKitConnection()

  return useAsyncMemo(
    async () => {
      if (!lastBlockNumber || !account || !hasPendingTxs) return null

      // Solana has no nonce, so there is nothing for wagmi to fetch and no replacement check to run.
      // Fallback to 0 on failure so receipt checking can still run even when the nonce fetch fails
      // (e.g. temporary RPC errors). The nonce-based replacement check will simply be skipped.
      const transactionsCount = isSolanaChain(chainId)
        ? SOLANA_UNUSED_NONCE
        : await getTransactionCount(config, { address: account }).catch(() => 0)

      const params: CheckEthereumTransactions = {
        chainId,
        isSafeWallet,
        lastBlockNumber,
        getReceipt,
        getTxSafeInfo,
        dispatch,
        nativeCurrencySymbol,
        cancelOrdersBatch,
        account,
        getTwapOrderById,
        transactionsCount,
        safeInfo,
        solanaConnection,
      }

      return params
    },
    [
      chainId,
      account,
      isSafeWallet,
      config,
      lastBlockNumber,
      dispatch,
      getReceipt,
      getTxSafeInfo,
      nativeCurrencySymbol,
      cancelOrdersBatch,
      getTwapOrderById,
      safeInfo,
      hasPendingTxs,
      solanaConnection,
    ],
    null,
  )
}
